import React, { useState, useEffect, useRef } from "react";
import { useAccessContext } from "@/contexts/AccessContext";
import { useMap } from "react-leaflet";
import GeoJsonLayer from "@/components/map/GeoJsonLayer";
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx";
import { createPortal } from "react-dom";
import { Switch, IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import DoNotDisturbOffIcon from "@mui/icons-material/DoNotDisturbOff";
import * as turf from "@turf/turf";
import ReactDOMServer from "react-dom/server";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { set } from "lodash";
import { coreGet } from "@/services/coreApi";

// Function to filter features based on their visibility within the current map viewport.
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds();
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry;
    // Determine visibility based on geometry type:
    // LineString: check if any segment point is within bounds
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) =>
        bounds.contains([coord[1], coord[0]]),
      );
      // MultiLineString: check each line segment
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) =>
        line.some((coord) => bounds.contains([coord[1], coord[0]])),
      );
      // Point: check if the point is within bounds
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]]);
      // Polygon: check each ring coordinate
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) =>
        ring.some((coord) => bounds.contains([coord[1], coord[0]])),
      );
      // Fallback: handle other geometry types generically
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]]);
      }
      return false;
    }
  });
  return filteredFeatures;
}

// Modern styled popup component rendered via a React Portal.
const ControlledPopup = ({ position, onClose, children }) => {
  const map = useMap();
  const [container] = useState(() => document.createElement("div"));

  useEffect(() => {
    container.style.position = "absolute";
    container.style.pointerEvents = "auto";
    // Anker unten-mittig wie beim Leaflet-Standard-Popup (Spitze zeigt auf den Punkt)
    container.style.transform = "translate(-50%, calc(-100% - 6px))";
    // Attach the container to Leaflet's popup pane.
    map.getPanes().popupPane.appendChild(container);
    return () => {
      container.remove();
    };
  }, [container, map]);

  useEffect(() => {
    const dblClickHandler = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    container.addEventListener("dblclick", dblClickHandler);
    return () => {
      container.removeEventListener("dblclick", dblClickHandler);
    };
  }, [container]);

  // Update container position based on geographic coordinate.
  const updatePopupPosition = () => {
    if (position) {
      const point = map.latLngToLayerPoint(position);
      container.style.left = `${point.x}px`;
      container.style.top = `${point.y}px`;
    }
  };

  useEffect(() => {
    updatePopupPosition();
    map.on("move", updatePopupPosition);
    map.on("zoom", updatePopupPosition);
    return () => {
      map.off("move", updatePopupPosition);
      map.off("zoom", updatePopupPosition);
    };
  }, [map, position, container]);

  if (!position) return null;

  return createPortal(
    <Box
      sx={{
        backgroundColor: "#fff",
        padding: "13px 24px 13px 20px",
        borderRadius: "12px",
        boxShadow: "0 3px 14px rgba(0,0,0,0.4)",
        position: "relative",
        minWidth: 150,
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          color: "#757575",
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
      {children}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: -6,
          width: 12,
          height: 12,
          transform: "translateX(-50%) rotate(45deg)",
          backgroundColor: "#fff",
        }}
      />
    </Box>,
    container,
  );
};

// Inline popup content with a Material‑UI Switch.
const PopupSwitchContent = ({ feature, onToggleOpen }) => {
  return (
    <Box>
      <Typography variant="inherit" sx={{ fontWeight: "bold" }}>
        {feature.properties.name || "Unnamed Area"}
      </Typography>
      <Typography variant="inherit">id: {feature.properties.id}</Typography>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="inherit" sx={{ mr: 1 }}>Open:</Typography>
        <Switch
          size="small"
          checked={!!feature.properties.open}
          onChange={(e) => onToggleOpen(feature, e.target.checked)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default function ViaAreas({
  cwViaAreasGeojson,
  setCwViaAreasGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  setVoyageActive,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext();
  const map = useMap();
  const suppressed = useGisSuppressed();
  const features = cwViaAreasGeojson?.features || [];
  // State for the currently selected feature with its popup position.
  const [selectedFeature, setSelectedFeature] = useState(null); // { feature, position }

  // Cluster + cycle tracking
  const viaClusterRef = useRef(null);
  const clearedThisCycleRef = useRef(false);
  const addedKeysRef = useRef(new Set());

  const accessRef = useRef({ accessContext, setAccessContext });
  accessRef.current = { accessContext, setAccessContext };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/via-areas",
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /geo/via-areas error=",
        );
        setCwViaAreasGeojson(data);
      } catch (error) {
        console.error(error);
      }
    };
    load();
  }, [setCwViaAreasGeojson]);

  // Create/remove marker cluster when ViaAreas or GIS toggle changes; clean up previous cluster
  useEffect(() => {
    // Remove previous cluster if present
    if (viaClusterRef.current) {
      viaClusterRef.current.clearLayers();
      map.removeLayer(viaClusterRef.current);
      viaClusterRef.current = null;
    }
    if (toggleFlags.includes("ViaAreas") && switchGisLayersActive) {
      viaClusterRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        // Dynamischer Radius: wird mit jedem Zoom kleiner → Cluster lösen sich früher
        maxClusterRadius: (zoom) => Math.max(60 - (zoom - 6) * 6, 20),
        // Clustering schon ab Zoom ≥ 11 deaktivieren (früheres Auflösen)
        disableClusteringAtZoom: 11,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          return L.divIcon({
            html: `<div style="
            background: rgba(255, 0, 0, 0.8);
            color: black;
            border-radius: 50%;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 0 0 5px rgba(255, 0, 0, 0.25);
            font-weight: 500;
            font-size: 14px;
          ">${count}</div>`,
            className: "custom-cluster-icon",
            iconSize: [36, 36],
            iconAnchor: [18, 18],
          });
        },
      });
      viaClusterRef.current.addTo(map);
    }
    return () => {
      if (viaClusterRef.current) {
        viaClusterRef.current.clearLayers();
        map.removeLayer(viaClusterRef.current);
        viaClusterRef.current = null;
      }
    };
  }, [toggleFlags, map, switchGisLayersActive]);

  // Reset per-cycle flags whenever inputs change (forces fresh rebuild of cluster contents)
  useEffect(() => {
    clearedThisCycleRef.current = false;
    addedKeysRef.current = new Set();
  }, [
    cwViaAreasGeojson,
    zoomLevel,
    dragged,
    resized,
    toggleFlags,
    switchGisLayersActive,
  ]);

  // Populate cluster: clear once per cycle, then add centroid markers for visible via-area polygons
  useEffect(() => {
    if (!toggleFlags.includes("ViaAreas")) return;
    if (!switchGisLayersActive) return;
    if (!viaClusterRef.current) return;

    // Clear cluster once per cycle before adding fresh markers
    if (!clearedThisCycleRef.current) {
      viaClusterRef.current.clearLayers();
      clearedThisCycleRef.current = true;
    }

    features.forEach((feature) => {
      // Only polygon/multipolygon features get a centroid marker
      if (!feature) return;
      const type = feature.geometry?.type;
      if (type !== "Polygon" && type !== "MultiPolygon") return;
      const isOpen = !!feature.properties?.open;

      // Compute centroid (fallback to bounds center)
      let latLng = null;
      try {
        const centroid = turf.centroid(feature);
        if (centroid?.geometry?.coordinates) {
          const [lng, lat] = centroid.geometry.coordinates;
          latLng = L.latLng(lat, lng);
        }
      } catch {}
      if (!latLng) {
        try {
          const b = L.geoJSON(feature).getBounds();
          latLng = b.getCenter();
        } catch {}
      }
      if (!latLng) return;

      // De-duplicate by stable key (include open state so toggles rebuild correctly)
      const fid =
        feature.properties?.id ||
        feature.id ||
        feature.properties?.uuid ||
        feature.properties?.gid;
      const fKeyBase =
        fid != null
          ? String(fid)
          : `${latLng.lat.toFixed(6)},${latLng.lng.toFixed(6)}`;
      const fKey = `${fKeyBase}|open:${isOpen}`;
      if (addedKeysRef.current.has(fKey)) return;
      addedKeysRef.current.add(fKey);

      const iconInner = isOpen ? (
        <DoNotDisturbOffIcon style={{ color: "green" }} />
      ) : (
        <DoNotDisturbOnIcon style={{ color: "red" }} />
      );
      const icon = L.divIcon({
        html: ReactDOMServer.renderToStaticMarkup(
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              background: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {iconInner}
          </div>,
        ),
        className: isOpen ? "open-area-icon" : "closed-area-icon",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker(latLng, { icon, interactive: !suppressed });
      if (!suppressed) {
        marker.on("click", () => {
          openPopup(feature, latLng);
        });
      }
      viaClusterRef.current.addLayer(marker);
    });
  }, [
    cwViaAreasGeojson,
    toggleFlags,
    map,
    zoomLevel,
    dragged,
    resized,
    switchGisLayersActive,
    suppressed,
  ]);

  // Ensure cluster is removed when ViaAreas toggle is off or GIS layers are globally disabled
  useEffect(() => {
    if (!toggleFlags.includes("ViaAreas") || !switchGisLayersActive) {
      if (viaClusterRef.current) {
        viaClusterRef.current.clearLayers();
        map.removeLayer(viaClusterRef.current);
        viaClusterRef.current = null;
      }
    }
  }, [toggleFlags, map, switchGisLayersActive]);

  // New helper: open popup and deactivate voyage
  const openPopup = (feature, position) => {
    setVoyageActive(false);
    setSelectedFeature({ feature, position });
  };

  // Update a feature's "open" property and update the global GeoJSON state.
  const updateFeatureOpen = (feature, newOpen) => {
    setVoyageActive(false);
    feature.properties.open = newOpen;
    setCwViaAreasGeojson((prevGeojson) => {
      if (!prevGeojson?.features) {
        return prevGeojson;
      }
      const updatedFeatures = prevGeojson.features.map((f) => {
        if (f.properties.id === feature.properties.id) {
          return { ...f, properties: { ...f.properties, open: newOpen } };
        }
        return f;
      });
      return {
        ...prevGeojson,
        features: updatedFeatures,
      };
    });
    if (
      selectedFeature &&
      selectedFeature.feature.properties.id === feature.properties.id
    ) {
      setSelectedFeature({ ...selectedFeature, feature: { ...feature } });
    }
  };

  // Instead of binding an imperative popup to each feature, listen for click events.
  const onEachFeature = (feature, layer) => {
    layer.on("click", () => {
      let position;
      if (layer.getLatLng) {
        position = layer.getLatLng();
      } else if (layer.getBounds) {
        position = layer.getBounds().getCenter();
      }
      openPopup(feature, position);
    });
  };

  const closePopup = () => {
    setSelectedFeature(null);
    setVoyageActive(true);
  };

  return !switchGisLayersActive ||
    !toggleFlags.includes("ViaAreas") ||
    cwViaAreasGeojson === null ? null : (
    <>
      <GeoJsonLayer
        key={[JSON.stringify(cwViaAreasGeojson), zoomLevel, dragged, resized]}
        data={filterViewportBoundingbox(map, features)}
        style={(feature) => ({
          fillColor: feature.properties.open ? "green" : "red",
          color: feature.properties.open ? "green" : "red",
          dashArray: "5,5",
        })}
        onEachFeature={onEachFeature}
        interactive
        transparent
      />
      {selectedFeature && (
        <ControlledPopup
          position={selectedFeature.position}
          onClose={closePopup}
        >
          <PopupSwitchContent
            feature={selectedFeature.feature}
            onToggleOpen={updateFeatureOpen}
          />
        </ControlledPopup>
      )}
    </>
  );
}
